import clsx from "clsx";
import { FunctionComponent } from "react";

import VergeHamburgerIcon from "@ui/components/brand/theverge/VergeHamburgerIcon";
import VergeLogo from "@ui/components/brand/theverge/VergeLogo";
import VergeSubscribeNav from "@ui/components/brand/theverge/VergeSubscribeNav";
import { type PinnedNavItem } from "@ui/components/navigation/Navigation/components/PrimaryNavigation";
import LinkMenu from "@ui/components/navigation/Navigation/components/PrimaryNavigation/components/PrimaryNavigationStandard/components/LinkMenu";
import * as usdStyles from "@ui/components/navigation/Navigation/components/PrimaryNavigation/components/PrimaryNavigationStandard/PrimaryNavigationStandard.css";
import { notificationsPopover } from "@ui/components/navigation/Navigation/config/Navigation.theverge.config.css";
import { VergeNavigationVariants } from "@ui/components/navigation/Navigation/Navigation";
import NotificationsButton from "@ui/components/navigation/Notifications/NotificationsButton";
import { useNavigationContext } from "@ui/context/navigation-context";
import { srOnly } from "@ui/shared-styles/accessibility.css";
import vergeLight from "@ui/themes/theverge-light.css";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import { useOptionalNotificationsDrawerContext } from "@voxmedia/duet-context/context/notifications-context";

import * as styles from "./VergeStandardNavigation.css";

export interface RightMenuButtonsDesktopProps {
  variant: "standard" | "homepage";
  monochrome?: boolean;
}

type Props = {
  Logo: FunctionComponent<{ className?: string }>;
  items: PinnedNavItem[];
  variant: VergeNavigationVariants;
  hideLogo?: boolean;
  showSubscribeInNav?: boolean;
};

const VergeStandardNavigation: FunctionComponent<Props> = ({
  items,
  Logo,
  variant = "standard",
  hideLogo = false,
  showSubscribeInNav = false,
}) => {
  const analytics = useAnalytics();
  const { openDrawer } = useNavigationContext();
  const notificationsDrawer = useOptionalNotificationsDrawerContext();

  const linkMenu = (
    <LinkMenu variant="standard">
      {items.map((item, index) => {
        if ("title" in item) {
          return (
            <LinkMenu.Link
              key={index}
              className={clsx(
                styles.linkMenuLink,
                styles.navTextColor[variant],
                styles.navTextHover,
              )}
              href={item.url}
              special={item.special}
              icon={item.icon}
              variant="standard"
              onClick={() =>
                analytics?.emit({
                  event: "navigation_click",
                  navigation_click: { name: item.title },
                })
              }
            >
              {item.title}
            </LinkMenu.Link>
          );
        }
        return item;
      })}

      <li
        className={clsx(
          styles.linkMenuLink,
          styles.navTextColor[variant],
          styles.notificationsNavItem,
        )}
      >
        <div className={styles.notificationsContainer}>
          <NotificationsButton
            Logo={VergeLogo}
            popoverId="primary-nav"
            popoverClassName={clsx(vergeLight, notificationsPopover)}
          />
        </div>
      </li>

      <LinkMenu.Link
        variant="standard"
        className={clsx(
          styles.linkMenuLink,
          styles.navTextColor[variant],

          styles.moreContainer,
        )}
        onClick={(e) => {
          e.preventDefault();
          notificationsDrawer?.closeDrawer();
          analytics?.emit({
            event: "navigation_click",
            navigation_click: { name: "more" },
          });
          openDrawer();
        }}
      >
        <VergeHamburgerIcon />
      </LinkMenu.Link>
    </LinkMenu>
  );

  const handleLogoClick = () =>
    analytics?.emit({
      event: "navigation_click",
      navigation_click: { name: "front page" },
    });

  return (
    <div
      className={
        usdStyles.container[variant === "homepage" ? "homepage" : "standard"]
      }
    >
      <div className={usdStyles.innerContainer}>
        {!hideLogo && (
          <div
            className={clsx(
              usdStyles.logoContainer["standard"],
              styles.logo[variant],
            )}
          >
            <a href="/" onClick={handleLogoClick}>
              <span className={srOnly}>The homepage</span>
              <Logo className={clsx(usdStyles.logo["standard"])} />
            </a>
          </div>
        )}

        {showSubscribeInNav && (
          <div className={styles.subscribeContainer}>
            <VergeSubscribeNav variant={variant} />
          </div>
        )}

        <nav
          className={clsx(
            usdStyles.linkMenuContainer["standard"],
            styles.linkMenuContainer[variant],
          )}
          aria-label="Top Navigation"
        >
          {linkMenu}
        </nav>
      </div>
    </div>
  );
};

export default VergeStandardNavigation;
